'use client'

import { useState } from 'react'

import { LoaderCircle } from 'lucide-react'

import { useBorderColor } from '../../hooks/useBorderColor'
import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { LevelToolbar } from '../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { arrayHasSameEl } from '@/shared/lib/array'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Media } from '@/shared/types/media'
import { Button } from '@/shared/ui/Button'



interface GameMosaicProps {
  step: ChallengeStep
  challengeId: number
}

type MosaicOption = {
  optionId: number | string
  image_id: number | null
  name: string
  is_correct: boolean
  image: Media
}

export function GameMosaic({ challengeId }: GameMosaicProps) {
  const {
    state,
    interactionDisabled,
    goNext,
    enableContinue,
    verifyChallenge,
    retryChallenge
  } = useFlow()

  const { getBorderColor } = useBorderColor()
  const { level, journeyAllowWrongAnswers, isSubmitting, onChallengeAnswer } =
    useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const meta = (challenge?.meta ?? {}) as Record<string, any>
  const options: MosaicOption[] = (
    challenge?.challenge_options?.length
      ? challenge.challenge_options.map(opt => ({
          optionId: opt.id,
          image_id: opt.meta?.image_id ?? null,
          name: opt.name ?? '',
          is_correct: opt.meta?.is_correct ?? false,
          image: opt.meta?.image ?? ({} as Media)
        }))
      : Array.isArray(meta?.images)
        ? (
            meta.images as {
              image_id?: number
              name?: string
              is_correct?: boolean
            }[]
          ).map((img, i) => ({
            optionId: `legacy-${i}`,
            image_id: img.image_id ?? null,
            name: img.name ?? '',
            is_correct: img.is_correct ?? false,
            image: (img as { image?: Media }).image ?? ({} as Media)
          }))
        : []
  ) as MosaicOption[]

  const [selectedOptionIds, setSelectedOptionIds] = useState<
    (number | string)[]
  >([])

  const toggleSelectOptionId = (optionId: number | string) => {
    if (isSubmitting || interactionDisabled) return

    setSelectedOptionIds(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    )
  }

  const validateAnswer = (selectedIds: (number | string)[]) => {
    const correctOptionIds = options
      .filter(opt => opt.is_correct)
      .map(opt => opt.optionId)
    return arrayHasSameEl(correctOptionIds, selectedIds)
  }

  const handleVerify = async () => {
    const answerIsCorrect = validateAnswer(selectedOptionIds)
    const skipValidation = Boolean(meta?.save_answer)
    const canAnswerChallenge =
      answerIsCorrect || skipValidation || journeyAllowWrongAnswers

    try {
      if (!challenge) return

      if (canAnswerChallenge) {
        await onChallengeAnswer({
          uid: challenge.uid,
          answer: selectedOptionIds.map(String).join(', '),
          answerIsCorrect,
          skipValidation
        })
        enableContinue()
      }

      verifyChallenge({ correct: answerIsCorrect || skipValidation })
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const handleRetry = () => {
    setSelectedOptionIds([])
    retryChallenge()
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>

      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <div className="grid w-full max-w-full mx-auto grid-cols-2 gap-2">
          {options.map((option, index) => {
            const isSelected = selectedOptionIds.includes(option.optionId)
            const borderColor = getBorderColor({
              hasSelection: true,
              isSelected
            })

            const src = option?.image?.file?.url ?? ''
            const hasImage = Boolean(src)

            return (
              <div
                key={option.optionId}
                className={`
                  aspect-square overflow-hidden rounded-xl border-2 bg-white
                  transition-colors duration-200 flex items-center justify-center
                  ${borderColor}
                  ${
                    interactionDisabled
                      ? 'opacity-50 pointer-events-none'
                      : 'hover:shadow-lg cursor-pointer'
                  }
                `}
                onClick={() => toggleSelectOptionId(option.optionId)}
                tabIndex={0}
                aria-pressed={isSelected}
                role="button"
              >
                {hasImage ? (
                  <img
                    src={src}
                    alt={option.name}
                    className="h-full w-full object-cover object-center select-none pointer-events-none"
                  />
                ) : (
                  <span className="text-center text-sm font-medium text-gray-600 px-2 break-words line-clamp-4">
                    {option.name || `Opção ${index + 1}`}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </HUD.Body>

      <LevelFooter>
        {state.phase === 'READY' && (
          <Button
            type="button"
            onClick={handleVerify}
            className="w-full font-bold"
            disabled={selectedOptionIds.length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>Verificar</span>
            )}
          </Button>
        )}

        {state.phase === 'CORRECT' && (
          <Button
            type="button"
            onClick={goNext}
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            <span>Continuar</span>
          </Button>
        )}

        {state.phase === 'LOCKED_WRONG' && (
          <Button
            type="button"
            onClick={journeyAllowWrongAnswers ? goNext : handleRetry}
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            <span>
              {journeyAllowWrongAnswers ? 'Entendido' : 'Tentar novamente'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}
